/**
 * Moteur de sons procéduraux (phase 5) : chaque effet est synthétisé à la
 * volée par la Web Audio API (oscillateurs + enveloppes + bruit blanc) — pas
 * un seul fichier audio. La simulation reste pure et déterministe : ce module
 * ne fait qu'OBSERVER des noms d'effets, il ne pilote jamais rien.
 *
 * Ces constantes sont des réglages de synthèse, pas des règles de gameplay :
 * elles restent volontairement en tête de ce fichier plutôt que dans
 * `@md/shared/constants`.
 */
const DEFAULT_MASTER_VOLUME = 0.6;
/** Anti-spam : un même effet ne peut pas être rejoué avant ce délai (secondes). */
const MIN_REPLAY_INTERVAL_S = 0.06;
/** Marge après la fin programmée d'une enveloppe, pour laisser `stop()` respirer. */
const STOP_MARGIN_S = 0.02;

const HUM_FREQ_MIN_HZ = 70;
const HUM_FREQ_MAX_HZ = 190;
const HUM_GAIN_MAX = 0.12;
const HUM_RAMP_S = 0.08;
/** Constante de lissage du fondu du gain maître (setEnabled). */
const MASTER_RAMP_S = 0.015;

/** Les treize effets sonores du jeu (concept phase 5). */
export const SFX_NAMES = [
  'ui',
  'jump',
  'land',
  'field-on',
  'field-off',
  'attract',
  'repel',
  'bolt',
  'switch',
  'door',
  'death',
  'victory',
  'star',
] as const;

export type SfxName = (typeof SFX_NAMES)[number];

/** Modulation ponctuelle d'un effet. */
export interface SfxOptions {
  /** Multiplicateur de volume (défaut 1). */
  readonly volume?: number;
  /** Multiplicateur de hauteur (défaut 1 = hauteur nominale de la recette). */
  readonly pitch?: number;
}

export interface SfxPlayer {
  /** Joue un effet. Ne lève jamais, même sans Web Audio. */
  play(name: SfxName, options?: SfxOptions): void;
  /** Active/désactive le son (persisté par l'appelant, pas ici). */
  setEnabled(enabled: boolean): void;
  enabled(): boolean;
  /** Reprend le contexte audio suspendu (à appeler sur le 1er geste utilisateur). */
  resume(): void;
  /**
   * Bourdonnement magnétique continu : la fréquence et le volume suivent
   * `intensity` ∈ [0, 1]. `null` coupe le bourdonnement.
   */
  setHum(intensity: number | null): void;
  destroy(): void;
}

export interface SfxPlayerOptions {
  /** Fabrique d'AudioContext — injectable pour les tests. `null` = audio indisponible. */
  readonly createContext?: () => AudioContext | null;
  readonly enabled?: boolean;
  readonly masterVolume?: number;
}

function defaultCreateContext(): AudioContext | null {
  const ctor =
    (
      globalThis as {
        AudioContext?: typeof AudioContext;
        webkitAudioContext?: typeof AudioContext;
      }
    ).AudioContext ??
    (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!ctor) {
    return null;
  }
  return new ctor();
}

/** Paramètres d'une voix oscillateur + enveloppe ADSR courte. */
interface ToneParams {
  readonly type: OscillatorType;
  readonly freqStart: number;
  /** Fréquence de fin ; omise = pas de glissando. */
  readonly freqEnd?: number;
  readonly start: number;
  readonly duration: number;
  readonly peakGain: number;
  readonly attack?: number;
}

/** Une note simple : oscillateur → gain (attaque courte, relâche exponentielle) → sortie. */
function tone(ctx: AudioContext, dest: AudioNode, params: ToneParams): void {
  const osc = ctx.createOscillator();
  osc.type = params.type;
  const gain = ctx.createGain();
  const attack = params.attack ?? Math.min(0.012, params.duration / 4);
  const end = params.start + params.duration;

  osc.frequency.setValueAtTime(Math.max(params.freqStart, 1), params.start);
  if (params.freqEnd !== undefined) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(params.freqEnd, 1), end);
  }

  gain.gain.setValueAtTime(0, params.start);
  gain.gain.linearRampToValueAtTime(Math.max(params.peakGain, 0.0001), params.start + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, end);

  osc.connect(gain);
  gain.connect(dest);
  osc.start(params.start);
  osc.stop(end + STOP_MARGIN_S);
}

interface NoiseParams {
  readonly start: number;
  readonly duration: number;
  readonly peakGain: number;
}

/** Une bouffée de bruit blanc → gain (percussif) → sortie. Utilisé par `land`/`death`. */
function noiseBurst(ctx: AudioContext, dest: AudioNode, params: NoiseParams): void {
  const frameCount = Math.max(1, Math.floor(ctx.sampleRate * params.duration));
  const buffer = ctx.createBuffer(1, frameCount, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frameCount; i += 1) {
    data[i] = Math.random() * 2 - 1;
  }

  const source = ctx.createBufferSource();
  source.buffer = buffer;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(Math.max(params.peakGain, 0.0001), params.start);
  gain.gain.exponentialRampToValueAtTime(0.0001, params.start + params.duration);

  source.connect(gain);
  gain.connect(dest);
  source.start(params.start);
  source.stop(params.start + params.duration + STOP_MARGIN_S);
}

type Recipe = (
  ctx: AudioContext,
  dest: AudioNode,
  now: number,
  volume: number,
  pitch: number,
) => void;

/**
 * Une recette par effet : chacune est une petite signature sonore
 * reconnaissable, entre 40 et 500 ms.
 */
const RECIPES: Readonly<Record<SfxName, Recipe>> = {
  ui: (ctx, dest, now, volume, pitch) => {
    tone(ctx, dest, {
      type: 'sine',
      freqStart: 720 * pitch,
      start: now,
      duration: 0.05,
      peakGain: 0.22 * volume,
    });
  },
  jump: (ctx, dest, now, volume, pitch) => {
    tone(ctx, dest, {
      type: 'square',
      freqStart: 220 * pitch,
      freqEnd: 460 * pitch,
      start: now,
      duration: 0.12,
      peakGain: 0.28 * volume,
    });
  },
  land: (ctx, dest, now, volume, pitch) => {
    noiseBurst(ctx, dest, { start: now, duration: 0.08, peakGain: 0.32 * volume });
    tone(ctx, dest, {
      type: 'sine',
      freqStart: 110 * pitch,
      freqEnd: 55 * pitch,
      start: now,
      duration: 0.09,
      peakGain: 0.22 * volume,
    });
  },
  'field-on': (ctx, dest, now, volume, pitch) => {
    tone(ctx, dest, {
      type: 'sine',
      freqStart: 180 * pitch,
      freqEnd: 440 * pitch,
      start: now,
      duration: 0.16,
      peakGain: 0.22 * volume,
    });
  },
  'field-off': (ctx, dest, now, volume, pitch) => {
    tone(ctx, dest, {
      type: 'sine',
      freqStart: 440 * pitch,
      freqEnd: 140 * pitch,
      start: now,
      duration: 0.14,
      peakGain: 0.2 * volume,
    });
  },
  attract: (ctx, dest, now, volume, pitch) => {
    tone(ctx, dest, {
      type: 'triangle',
      freqStart: 260 * pitch,
      freqEnd: 560 * pitch,
      start: now,
      duration: 0.18,
      peakGain: 0.26 * volume,
    });
  },
  repel: (ctx, dest, now, volume, pitch) => {
    tone(ctx, dest, {
      type: 'sawtooth',
      freqStart: 560 * pitch,
      freqEnd: 140 * pitch,
      start: now,
      duration: 0.2,
      peakGain: 0.3 * volume,
    });
  },
  bolt: (ctx, dest, now, volume, pitch) => {
    tone(ctx, dest, {
      type: 'square',
      freqStart: 660 * pitch,
      start: now,
      duration: 0.06,
      peakGain: 0.26 * volume,
    });
    tone(ctx, dest, {
      type: 'square',
      freqStart: 990 * pitch,
      start: now + 0.06,
      duration: 0.09,
      peakGain: 0.3 * volume,
    });
  },
  switch: (ctx, dest, now, volume, pitch) => {
    tone(ctx, dest, {
      type: 'square',
      freqStart: 880 * pitch,
      start: now,
      duration: 0.04,
      peakGain: 0.2 * volume,
      attack: 0.002,
    });
  },
  door: (ctx, dest, now, volume, pitch) => {
    tone(ctx, dest, {
      type: 'sawtooth',
      freqStart: 160 * pitch,
      freqEnd: 90 * pitch,
      start: now,
      duration: 0.4,
      peakGain: 0.2 * volume,
    });
  },
  death: (ctx, dest, now, volume, pitch) => {
    noiseBurst(ctx, dest, { start: now, duration: 0.3, peakGain: 0.34 * volume });
    tone(ctx, dest, {
      type: 'sawtooth',
      freqStart: 200 * pitch,
      freqEnd: 45 * pitch,
      start: now,
      duration: 0.4,
      peakGain: 0.24 * volume,
    });
  },
  victory: (ctx, dest, now, volume, pitch) => {
    const notes = [523.25, 659.25, 783.99, 1046.5];
    const noteDuration = 0.1;
    for (const [i, freq] of notes.entries()) {
      tone(ctx, dest, {
        type: 'sine',
        freqStart: freq * pitch,
        start: now + i * noteDuration,
        duration: noteDuration,
        peakGain: 0.26 * volume,
      });
    }
  },
  star: (ctx, dest, now, volume, pitch) => {
    tone(ctx, dest, {
      type: 'triangle',
      freqStart: 1200 * pitch,
      freqEnd: 1900 * pitch,
      start: now,
      duration: 0.12,
      peakGain: 0.22 * volume,
    });
  },
};

/**
 * Construit un lecteur de sons procéduraux.
 *
 * L'`AudioContext` n'est créé qu'au premier appel utile (`play`/`resume`/
 * `setHum`) : en construire un plus tôt déclenche des avertissements
 * navigateur (règle des contextes audio suspendus tant qu'aucun geste
 * utilisateur n'a eu lieu). Toute erreur — absence de Web Audio, fabrique qui
 * lève — dégrade silencieusement le lecteur en no-op : `play()` ne lève
 * jamais.
 */
export function createSfxPlayer(options: SfxPlayerOptions = {}): SfxPlayer {
  const makeContext = options.createContext ?? defaultCreateContext;
  const masterVolume = options.masterVolume ?? DEFAULT_MASTER_VOLUME;
  let enabledFlag = options.enabled ?? true;

  let ctx: AudioContext | null = null;
  let master: GainNode | null = null;
  let humOsc: OscillatorNode | null = null;
  let humGain: GainNode | null = null;
  const lastPlayedAt = new Map<SfxName, number>();

  const ensureContext = (): AudioContext | null => {
    if (ctx) {
      return ctx;
    }
    let created: AudioContext | null;
    try {
      created = makeContext();
    } catch {
      created = null;
    }
    if (!created) {
      return null;
    }
    ctx = created;
    master = ctx.createGain();
    master.gain.value = enabledFlag ? masterVolume : 0;
    master.connect(ctx.destination);
    return ctx;
  };

  const stopHum = (): void => {
    if (humOsc) {
      try {
        humOsc.stop();
      } catch {
        // déjà arrêté : sans conséquence.
      }
      humOsc.disconnect();
    }
    if (humGain) {
      humGain.disconnect();
    }
    humOsc = null;
    humGain = null;
  };

  return {
    play(name: SfxName, opts: SfxOptions = {}): void {
      if (!enabledFlag) {
        return;
      }
      const context = ensureContext();
      if (!context || !master) {
        return;
      }
      const now = context.currentTime;
      const last = lastPlayedAt.get(name);
      if (last !== undefined && now - last < MIN_REPLAY_INTERVAL_S) {
        return;
      }
      lastPlayedAt.set(name, now);
      const volume = opts.volume ?? 1;
      const pitch = opts.pitch ?? 1;
      try {
        RECIPES[name](context, master, now, volume, pitch);
      } catch {
        // La Web Audio API peut lever (contexte fermé, navigateur bogué…) :
        // `play()` ne doit jamais faire tomber l'appelant.
      }
    },

    setEnabled(enabled: boolean): void {
      enabledFlag = enabled;
      if (ctx && master) {
        master.gain.setTargetAtTime(enabled ? masterVolume : 0, ctx.currentTime, MASTER_RAMP_S);
      }
    },

    enabled(): boolean {
      return enabledFlag;
    },

    resume(): void {
      const context = ensureContext();
      if (!context) {
        return;
      }
      try {
        const result = context.resume();
        if (result && typeof result.catch === 'function') {
          result.catch(() => {
            // Reprise refusée (pas de geste utilisateur…) : sans conséquence.
          });
        }
      } catch {
        // certains contextes factices (tests) n'implémentent pas resume().
      }
    },

    setHum(intensity: number | null): void {
      const context = ensureContext();
      if (!context || !master) {
        return;
      }
      if (intensity === null) {
        stopHum();
        return;
      }
      const clamped = Math.max(0, Math.min(1, intensity));
      if (!humOsc || !humGain) {
        humOsc = context.createOscillator();
        humOsc.type = 'sine';
        humGain = context.createGain();
        humGain.gain.value = 0;
        humOsc.connect(humGain);
        humGain.connect(master);
        humOsc.start(context.currentTime);
      }
      const now = context.currentTime;
      const freq = HUM_FREQ_MIN_HZ + clamped * (HUM_FREQ_MAX_HZ - HUM_FREQ_MIN_HZ);
      humOsc.frequency.setTargetAtTime(freq, now, HUM_RAMP_S);
      humGain.gain.setTargetAtTime(clamped * HUM_GAIN_MAX, now, HUM_RAMP_S);
    },

    destroy(): void {
      stopHum();
      lastPlayedAt.clear();
      if (ctx) {
        try {
          ctx.close();
        } catch {
          // contexte déjà fermé ou fabrique factice : sans conséquence.
        }
      }
      ctx = null;
      master = null;
    },
  };
}
